import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import type { RevisionResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { resolveCitationDocument } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/resolveCitationDocument'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import React, { useCallback } from 'react'

import { DocChatRevisionDiffCard } from './DocChatRevisionDiffCard'
import { DocChatRevisionMissingCard } from './DocChatRevisionMissingCard'
import type {
  NavigateDocumentHandler,
  PreviewRevisionHandler,
} from './previewDocumentHandler'

interface RevisionTarget {
  documentId: string
  pageNumber: number | null
}

function targetOf(result: RevisionResult): RevisionTarget | undefined {
  if (result.kind === 'diff') {
    return {
      documentId: result.to_document_id,
      pageNumber: result.to_page_num ?? null,
    }
  }
  if (!result.jump_ref) return undefined
  return {
    documentId: result.jump_ref.document_id,
    pageNumber: result.jump_ref.page_num ?? null,
  }
}

function RevisionResultCard({
  result,
  onNavigateDocument,
  onPreviewRevision,
}: {
  result: RevisionResult
  onNavigateDocument?: NavigateDocumentHandler
  onPreviewRevision?: PreviewRevisionHandler
}): React.ReactElement {
  const { citationDocumentLookup, documentResults } = useDocumentSearchData()
  const { selectedPipelineConfigId } = usePipelineConfig()
  const target = targetOf(result)
  const row: DocumentTableRow | undefined = target
    ? resolveCitationDocument({
        citationDocumentId: target.documentId,
        documentResults,
        pipelineConfigId: selectedPipelineConfigId,
        lookup: citationDocumentLookup,
      })
    : undefined
  const canNavigate = row !== undefined && onNavigateDocument !== undefined
  const pageNumber = target?.pageNumber ?? undefined

  const handleNavigate = useCallback(() => {
    if (!row || !onNavigateDocument) return
    // Open in a new tab so the chat stays put.
    onNavigateDocument(row, true, false, { pageNumber })
  }, [onNavigateDocument, pageNumber, row])

  const handlePreview = useCallback(() => {
    if (result.kind !== 'diff' || !onPreviewRevision) return
    // Always openable: whether the pair resolves needs the details fetch, which
    // the panel runs and reports as unavailable instead of showing a wrong pair.
    onPreviewRevision({
      fromDocumentId: result.from_document_id,
      fromPageNum: result.from_page_num ?? null,
      toDocumentId: result.to_document_id,
      toPageNum: result.to_page_num ?? null,
    })
  }, [onPreviewRevision, result])

  return result.kind === 'diff' ? (
    <DocChatRevisionDiffCard
      result={result}
      canOpen={onPreviewRevision !== undefined}
      onOpen={handlePreview}
    />
  ) : (
    <DocChatRevisionMissingCard
      result={result}
      canOpen={canNavigate}
      onOpen={handleNavigate}
    />
  )
}

export function DocChatRevisionResults({
  results,
  onNavigateDocument,
  onPreviewRevision,
}: {
  results: readonly RevisionResult[]
  onNavigateDocument?: NavigateDocumentHandler
  onPreviewRevision?: PreviewRevisionHandler
}): React.ReactElement | null {
  if (results.length === 0) return null
  return (
    <>
      {results.map((result, index) => (
        <RevisionResultCard
          key={`${result.kind}-${index}`}
          result={result}
          onNavigateDocument={onNavigateDocument}
          onPreviewRevision={onPreviewRevision}
        />
      ))}
    </>
  )
}
